import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuilttResolvable, useSession } from '@quiltt/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useIsUnreliableInstitution } from './unreliableInstitutions';

// Sandbox workaround: Map certain banks to sandbox institution ID for testing Quiltt resolvability.
// In non-prod, Bank of America and Wells Fargo use sandbox ID so we can test resolvable path.
const SANDBOX_PLAID_INSTITUTION_ID = 'ins_109508'; // First Platypus Bank (Plaid sandbox)
const SANDBOX_RESOLVABLE_INSTITUTIONS = ['bank of america', 'wells fargo'];

function isSandboxStandIn(institutionName: string): boolean {
    return (
        import.meta.env.VITE_ENVIRONMENT !== 'production' &&
        SANDBOX_RESOLVABLE_INSTITUTIONS.includes(institutionName.toLowerCase())
    );
}

export function getPlaidInstitutionIdForResolvability(institutionId: string, institutionName: string): string {
    return isSandboxStandIn(institutionName) ? SANDBOX_PLAID_INSTITUTION_ID : institutionId;
}

/**
 * The connector search term for an institution, or undefined to open the connector on its own search.
 *
 * A sandbox stand-in gets no term: it checks as resolvable only by borrowing the sandbox institution's
 * id, and neither its own name nor the sandbox institution's matches a statements-capable institution
 * in the sandbox connector. A term that matches nothing is worse than none, because the connector then
 * skips its search screen and dead-ends on "Institution Not Supported".
 */
export function getInstitutionSearchForConnector(institutionName: string): string | undefined {
    return isSandboxStandIn(institutionName) ? undefined : institutionName;
}

/**
 * Checks Quiltt resolvability for a single institution and reports the result.
 * Renders nothing — must be rendered inside a QuilttProvider.
 *
 * When `skipAsResolvable` is true, immediately reports the institution as resolvable
 * without performing a Quiltt check (used for institutions that already have statements enabled).
 * Otherwise a bank on `quiltt_unreliable_institutions` reports not resolvable without a Quiltt call.
 *
 * `checkResolvable` resolves true/false on success, null on error (non-200, thrown, no provider id).
 * true/false is always reported; a completed null reports false with `isError` only with
 * `treatErrorAsUnsupported` (onboarding surfaces) — by default (Settings/TaskList) it stays unreported.
 * A never-resolving check reports nothing; the onboarding step's timeout covers that (#3505).
 */
export function InstitutionResolvabilityChecker({
    institutionId,
    institutionName,
    connectorId,
    skipAsResolvable = false,
    treatErrorAsUnsupported = false,
    onResult,
}: {
    institutionId: string;
    institutionName: string;
    connectorId: string;
    skipAsResolvable?: boolean;
    treatErrorAsUnsupported?: boolean;
    onResult: (institutionId: string, isResolvable: boolean, isError?: boolean) => void;
}) {
    const [session] = useSession();
    const isUnreliableInstitution = useIsUnreliableInstitution(institutionId);
    const [hasChecked, setHasChecked] = useState(false);
    const connectorCheck = useQuilttResolvable(connectorId);
    const { checkResolvable } = connectorCheck;

    useEffect(() => {
        if (skipAsResolvable) {
            onResult(institutionId, true);
            return;
        }

        if (hasChecked) {
            return;
        }

        if (isUnreliableInstitution) {
            setHasChecked(true);
            onResult(institutionId, false);
            return;
        }

        // Guard on connectorId too: if the backend hasn't populated it yet, checkResolvable bails internally
        // and returns null — but hasChecked would flip and the check would never retry. Waiting until
        // connectorId arrives lets the check actually run.
        if (!session || !institutionId || !connectorId) {
            return;
        }

        setHasChecked(true);
        const plaidIdForCheck = getPlaidInstitutionIdForResolvability(institutionId, institutionName);
        checkResolvable({ plaid: plaidIdForCheck }).then((result) => {
            if (result === true || result === false) {
                onResult(institutionId, result);
            } else if (treatErrorAsUnsupported) {
                onResult(institutionId, false, true);
            }
        });
    }, [
        session,
        institutionId,
        institutionName,
        hasChecked,
        checkResolvable,
        skipAsResolvable,
        treatErrorAsUnsupported,
        onResult,
        connectorId,
        isUnreliableInstitution,
    ]);

    return null;
}

/**
 * Hook to manage resolvability check results for multiple institutions.
 * Returns the results map and a stable callback for updating it.
 */
export function useResolvabilityResults() {
    const [results, setResults] = useState<Record<string, boolean>>({});

    const handleResult = useCallback((institutionId: string, isResolvable: boolean) => {
        setResults((prev) => ({ ...prev, [institutionId]: isResolvable }));
    }, []);

    return { results, handleResult };
}

const resolvabilityQueryKey = (cacheKey: string | undefined) => ['quiltt', 'resolvability', cacheKey ?? 'pending'];
const unknownResolvabilityQueryKey = (cacheKey: string | undefined) => [...resolvabilityQueryKey(cacheKey), 'unknown'];

/**
 * Cache-backed variant of {@link useResolvabilityResults}: persists answers in the React Query cache (keyed
 * by connector) so they survive re-mounts (leaving/re-entering the step) and the flaky Quiltt check (#3505)
 * isn't re-run. An errored check is stored as `false` so boolean consumers keep their fallback, but is also
 * recorded as unknown (see {@link useUnknownResolvability}) until a real answer replaces it. A
 * timeout/failsafe "not supported" must NOT be written here, or a merely-slow but supported institution is
 * remembered as unsupported forever.
 */
export function useCachedResolvabilityResults(cacheKey: string | undefined) {
    const queryClient = useQueryClient();
    const queryKey = useMemo(() => resolvabilityQueryKey(cacheKey), [cacheKey]);

    const { data: results = {} } = useQuery<Record<string, boolean>>({
        queryKey,
        // Pure client-side cache store — never fetches; results are written via setQueryData below.
        queryFn: () => ({}),
        enabled: false,
        staleTime: Infinity,
        gcTime: Infinity,
        initialData: {},
    });

    const handleResult = useCallback(
        (institutionId: string, isResolvable: boolean, isError = false) => {
            queryClient.setQueryData<Record<string, boolean>>(queryKey, (prev = {}) => ({
                ...prev,
                [institutionId]: isResolvable,
            }));
            queryClient.setQueryData<string[]>(unknownResolvabilityQueryKey(cacheKey), (prev = []) => {
                const rest = prev.filter((id) => id !== institutionId);
                return isError ? [...rest, institutionId] : rest;
            });
        },
        [queryClient, queryKey, cacheKey]
    );

    return { results, handleResult };
}

/**
 * Institutions whose support is unknown — the check errored or timed out — kept in the same connector-keyed
 * cache so every step of the flow agrees on them. Cleared per institution when a real answer lands.
 */
export function useUnknownResolvability(cacheKey: string | undefined) {
    const queryClient = useQueryClient();
    const queryKey = useMemo(() => unknownResolvabilityQueryKey(cacheKey), [cacheKey]);

    const { data: unknownList = [] } = useQuery<string[]>({
        queryKey,
        queryFn: () => [],
        enabled: false,
        staleTime: Infinity,
        gcTime: Infinity,
        initialData: [],
    });
    const unknownIds = useMemo<ReadonlySet<string>>(() => new Set(unknownList), [unknownList]);

    const markUnknown = useCallback(
        (institutionIds: string[]) => {
            queryClient.setQueryData<string[]>(queryKey, (prev = []) => [...new Set([...prev, ...institutionIds])]);
        },
        [queryClient, queryKey]
    );

    return { unknownIds, markUnknown };
}
